// The instrument seam (#236 slice 1): "candidate voicings for chord X" as an
// instrument-parameterised question. The app grew up guitar-shaped — grips,
// string sets, fret distance — but the things that make it interesting are
// not: the voice-leading engines are a SEARCH × METRIC registry whose pitch
// metric never looks at a fret, and a voicing-family selection is just a
// search space. This module names the currency those layers actually need
// (`InstrumentVoicing`: sounding pitches + identity, physical realization
// optional) and registers each instrument's enumerator behind it.
//
// Guitar's enumerator DELEGATES to the existing grip machinery byte-for-byte
// (pinned by a parity test) — this seam adds a vocabulary, not a behaviour
// change. Piano's proves the seam is real: pitch-set candidates with a
// hand-span constraint where the guitar has a fret span, no fingering
// anywhere, and the pitch metric drives voice leading over them unchanged.
//
// NOT yet wired into the optimisers/UI: the Sequence Builder's candidate
// space still enumerates grips directly (its search options — cross-set,
// octave variants, melody fallbacks — are guitar-search concerns living in
// voiceLeading.ts). Later slices consume this registry from there.

import { chordSymbol, type ChordTypeId } from './chords';
import {
  generateChordVoicing,
  generateChordVoicingFromNotes,
  shouldUseVoicing,
  type ChordVoicing,
  type Inversion,
} from './voicing';
import { soundingMidis } from './voiceLeading';
import type { StringSet } from './tuning';
import { VOICING_FAMILY_IDS, type VoicingFamily } from './voicingFamilies';
import { enumerateJoePassVoicings } from './joePassVoicings';
import {
  enumeratePiano,
  PIANO_FAMILIES,
  type PianoEnumerateOptions,
  type PianoFamily,
} from './pianoVoicings';

export type InstrumentId = 'guitar' | 'piano';

/**
 * One candidate voicing, in the vocabulary every instrument shares: what
 * SOUNDS (midis low→high — the pitch metric's whole world), what it is
 * called, and which catalogue entry produced it. The physical realization is
 * per-instrument: a guitar candidate carries its grip (diagrams, the fret
 * metric); a piano candidate carries its hand span.
 */
export interface InstrumentVoicing {
  /** Sounding pitches, low→high. */
  midis: number[];
  /** Note names low→high (engine-internal sharp spelling). */
  voicing: string[];
  inversion: Inversion;
  /** Family id, scoped to the instrument ('drop-2' means nothing on piano). */
  family: string;
  /** Display symbol, e.g. "Ebmaj7". */
  symbol: string;
  /** Guitar only: the physical grip. Absence means "no frets exist", not
   * "unknown" — piano candidates are complete without one. */
  fingering?: ChordVoicing;
  /** Piano only: bottom-to-top reach in semitones (per hand). */
  span?: number;
  /** Piano two-hand / upper-structure / quartal families only: which pitches
   * each hand takes. */
  hands?: { left: number[]; right: number[] };
  /** Piano upper-structure family only: which triad the right hand plays,
   * spelled against the chord's root. Guitar never sets it. */
  upperTriad?: { root: string; quality: 'major' | 'minor' };
}

export interface EnumerateOptions {
  /** Apply the avoid-b9 rule (default true, both instruments). */
  avoidB9?: boolean;
  /** Guitar: which register the grip lives in (default 'middle'). */
  stringSet?: StringSet;
  /** Guitar: an explicit four-note realization (the melody-led path) in
   * place of the catalogue's chord tones. */
  voicingNotes?: string[];
  /** Piano: one hand's reach in semitones (default 14, a major 9th). */
  maxHandSpan?: number;
  /** Piano: register bounds for the whole stack (default C3–C6). */
  register?: { low: number; high: number };
  /** Piano: the root AS WRITTEN, used to spell the voicing diatonically.
   * Defaults to `rootName`. */
  displayRoot?: string;
}

export interface Instrument {
  id: InstrumentId;
  label: string;
  /** The family ids this instrument's catalogue knows. */
  families: readonly string[];
  /**
   * Candidates for one chord × inversion × family. Guitar yields at most one
   * (the grip finder returns the first playable shape — enumerating
   * alternatives is its own backlog item); piano yields one per octave
   * placement that fits the register and the hand.
   */
  enumerate(
    rootName: string,
    chordType: ChordTypeId,
    inversion: Inversion,
    family: string,
    options?: EnumerateOptions,
  ): InstrumentVoicing[];
}

const guitar: Instrument = {
  id: 'guitar',
  label: 'Guitar',
  families: VOICING_FAMILY_IDS,
  enumerate(rootName, chordType, inversion, family, options = {}) {
    if (!(VOICING_FAMILY_IDS as readonly string[]).includes(family)) return [];
    const stringSet = options.stringSet ?? 'middle';
    if (family === 'joe-pass') {
      return enumerateJoePassVoicings(rootName, chordType, options)
        .filter(
          (grip) =>
            grip.inversion === inversion &&
            shouldUseVoicing(grip, grip.stringSet, chordType, options.avoidB9 ?? true),
        )
        .map((grip) => ({
          midis: soundingMidis(grip),
          voicing: grip.voicing,
          inversion,
          family,
          symbol: grip.symbol,
          fingering: grip,
        }));
    }
    const grip = options.voicingNotes
      ? generateChordVoicingFromNotes(
          rootName,
          chordType,
          options.voicingNotes,
          inversion,
          stringSet,
          family as VoicingFamily,
        )
      : generateChordVoicing(rootName, chordType, inversion, stringSet, family as VoicingFamily);
    if (!grip) return [];
    // The b9 rule the grips already obey — same gate `usableInversion` applies.
    if (options.avoidB9 !== false && !shouldUseVoicing(grip, stringSet, chordType, true)) {
      return [];
    }
    return [
      {
        midis: soundingMidis(grip),
        voicing: grip.voicing,
        inversion,
        family,
        symbol: grip.symbol,
        fingering: grip,
      },
    ];
  },
};

const piano: Instrument = {
  id: 'piano',
  label: 'Piano',
  families: PIANO_FAMILIES,
  enumerate(rootName, chordType, inversion, family, options = {}) {
    if (!(PIANO_FAMILIES as readonly string[]).includes(family)) return [];
    const pianoOptions: PianoEnumerateOptions = {
      avoidB9: options.avoidB9,
      maxHandSpan: options.maxHandSpan,
      register: options.register,
      displayRoot: options.displayRoot,
    };
    return enumeratePiano(rootName, chordType, inversion, family as PianoFamily, pianoOptions).map(
      (v) => ({
        midis: v.midis,
        voicing: v.voicing,
        inversion: v.inversion,
        family: v.family,
        symbol: chordSymbol(options.displayRoot ?? rootName, chordType),
        span: v.span,
        hands: v.hands,
        upperTriad: v.upperTriad,
      }),
    );
  },
};

export const INSTRUMENTS: readonly Instrument[] = [guitar, piano];

export function instrumentById(id: InstrumentId): Instrument {
  const found = INSTRUMENTS.find((instrument) => instrument.id === id);
  if (!found) throw new Error(`Unknown instrument: ${id}`);
  return found;
}
